import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SessionActivityService {
  private readonly activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
  private readonly checkIntervalMs = 60 * 1000;
  private hasStarted = false;
  private intervalID: number | null = null;
  private lastTouchAt = 0;

  constructor(
    private authService: AuthService,
    private router: Router,
    private zone: NgZone
  ) {}

  start(): void {
    if (this.hasStarted) {
      return;
    }

    this.hasStarted = true;

    this.zone.runOutsideAngular(() => {
      this.activityEvents.forEach(eventName => {
        window.addEventListener(eventName, this.handleActivity, { passive: true });
      });

      window.addEventListener('focus', this.handleSessionCheck);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('storage', this.handleSessionCheck);
      this.intervalID = window.setInterval(this.handleSessionCheck, this.checkIntervalMs);
    });
  }

  stop(): void {
    if (!this.hasStarted) {
      return;
    }

    this.activityEvents.forEach(eventName => {
      window.removeEventListener(eventName, this.handleActivity);
    });

    window.removeEventListener('focus', this.handleSessionCheck);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('storage', this.handleSessionCheck);

    if (this.intervalID) {
      window.clearInterval(this.intervalID);
      this.intervalID = null;
    }

    this.hasStarted = false;
  }

  private handleActivity = (): void => {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    const now = Date.now();
    if (now - this.lastTouchAt < 30 * 1000) {
      return;
    }

    this.lastTouchAt = now;
    this.authService.touchSession();
  };

  private handleVisibilityChange = (): void => {
    if (!document.hidden) {
      this.handleSessionCheck();
    }
  };

  private handleSessionCheck = (): void => {
    if (!this.authService.getToken()) {
      return;
    }

    const publicPaths = ['/', '/departures', '/login', '/register', '/verify-email'];
    const currentPath = this.router.url.split('?')[0];
    if (publicPaths.includes(currentPath) || currentPath.startsWith('/tickets/')) {
      return;
    }

    if (!this.authService.isAuthenticated()) {
      this.zone.run(() => {
        this.router.navigate(['/login'], {
          queryParams: { sessionExpired: true }
        });
      });
    }
  };
}
